import type { SkillFileNode } from '@onefootprint/request-types/dashboard';
import {
  addFileToFolder,
  addFolderToRoot,
  dropExternalNodes,
  moveNodeTo,
  removeNodeAt,
  renameNodeAt,
  type SkillsOpResult,
  setFileContentAt,
} from './skills-operations';
import type { DropPosition } from './skills-path';

type SkillsActions = {
  addFolder: () => void;
  addFile: (parentPath: string) => void;
  remove: (path: string) => void;
  rename: (path: string, name: string, options?: { save?: boolean }) => void;
  setContent: (path: string, content: string, options?: { save?: boolean }) => void;
  move: (fromPath: string, toPath: string, position: DropPosition) => void;
  dropExternal: (targetPath: string | null, position: DropPosition | 'root', nodes: SkillFileNode[]) => void;
  select: (path: string | null) => void;
};

type Args = {
  getState: () => { skills: SkillFileNode[]; selectedPath: string | null };
  apply: (result: SkillsOpResult) => void;
  save: (skills: SkillFileNode[]) => void;
  select: (path: string | null) => void;
};

/** Wires the pure tree operations to a store: `apply` updates it, `save` persists a structural change. */
const buildSkillsActions = ({ getState, apply, save, select }: Args): SkillsActions => {
  const applyAndSave = (result: SkillsOpResult) => {
    apply(result);
    save(result.skills);
  };

  return {
    addFolder: () => {
      applyAndSave(addFolderToRoot(getState().skills));
    },

    addFile: parentPath => {
      const result = addFileToFolder(getState().skills, parentPath);
      if (result) applyAndSave(result);
    },

    remove: path => {
      const { skills, selectedPath } = getState();
      applyAndSave(removeNodeAt(skills, path, selectedPath));
    },

    rename: (path, name, options) => {
      const result = renameNodeAt(getState().skills, path, name);
      if (options?.save) applyAndSave(result);
      else apply(result);
    },

    setContent: (path, content, options) => {
      const result = setFileContentAt(getState().skills, path, content);
      if (options?.save) applyAndSave(result);
      else apply(result);
    },

    move: (fromPath, toPath, position) => {
      const { skills } = getState();
      const result = moveNodeTo(skills, fromPath, toPath, position);
      if (result.skills !== skills) applyAndSave(result);
    },

    dropExternal: (targetPath, position, nodes) => {
      const { skills, selectedPath } = getState();
      applyAndSave(dropExternalNodes(skills, { targetPath, position, nodes, currentSelectedPath: selectedPath }));
    },

    select,
  };
};

export type { SkillsActions };
export default buildSkillsActions;
